/**
 * The job's own notes (`wo_notes`) as a log: the advisor's intake note —
 * what the customer said, where the key is — and whatever the crew adds.
 * One timeline per job; the per-service notes are gone (2026-09-22). Any
 * technician on the job may write; a sent note is final on the floor —
 * only a manager rewrites or removes one (user call 2026-09-22), and the
 * technician routes for that no longer exist.
 *
 * The log itself is `NoteThread` from the shared kit — the manager's page
 * renders the same one — so this file only maps the wire shape, hands
 * over the labels and owns the add mutation.
 *
 * Stamped with the day when the note is older than today: the advisor
 * often writes it the evening before.
 */
import { NoteThread, type ThreadNote } from '@vollgas/shared/ui'
import type { WoDetailNote } from '@/apis/types'
import { useI18n } from '@/i18n/TranslationContext'
import { noteStamp } from '@/lib/format'
import { useAddWorkOrderNote } from '@/hooks/useWorkshop'

interface Props {
  readonly workOrderId: string
  readonly notes: readonly WoDetailNote[]
  /** A cancelled job takes no more notes. */
  readonly readOnly?: boolean
  readonly className?: string
}

export function WoJobNotes({ workOrderId, notes, readOnly = false, className }: Props) {
  const { t, lang } = useI18n()
  const addNote = useAddWorkOrderNote()

  const thread: ThreadNote[] = notes.map((n) => ({
    id: n.id,
    body: n.body,
    stamp: noteStamp(n, lang),
    authorName: n.authorName,
    mine: n.mine,
  }))

  return (
    <NoteThread
      notes={thread}
      labels={{
        title: t.workOrder.jobNotes,
        placeholder: t.workOrder.addNote,
        send: t.workOrder.sendNote,
        edit: t.common.edit,
        delete: t.common.delete,
        save: t.common.save,
        cancel: t.common.cancel,
        you: t.tasks.you,
        empty: t.workOrder.noNotes,
        error: t.workOrder.noteError,
        preview: t.workOrder.notePreview,
      }}
      pending={addNote.isPending}
      onAdd={(body) => addNote.mutateAsync({ workOrderId, body })}
      notesEditable="none"
      readOnly={readOnly}
      listClassName="max-h-[22rem] lg:max-h-none"
      className={className}
    />
  )
}
